import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';

const navItems = [
  { path: '/dashboard', icon: 'ri-dashboard-line', label: 'Dashboard' },
  { path: '/calendar', icon: 'ri-calendar-line', label: 'Calendar' },
  { path: '/statistics', icon: 'ri-bar-chart-line', label: 'Statistics' },
  { path: '/call-log', icon: 'ri-phone-line', label: 'Call Log' },
  { path: '/habits', icon: 'ri-list-check-3', label: 'Habits' },
  { path: '/settings', icon: 'ri-settings-3-line', label: 'Settings' },
];

/**
 * Shared responsive app shell: a collapsible sidebar (drawer on mobile,
 * static on desktop) plus a mobile top bar with a hamburger. Every signed-in
 * page renders its content inside this so navigation works on any screen size.
 */
export default function AppLayout({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const initials =
    user?.user_metadata?.full_name?.charAt(0)?.toUpperCase() ||
    user?.email?.charAt(0)?.toUpperCase() ||
    '?';

  const themeToggle = (
    <button
      onClick={toggleTheme}
      className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-500 hover:text-gold hover:bg-gold/10 transition cursor-pointer"
      aria-label="Toggle dark mode"
      title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      <i className={theme === 'dark' ? 'ri-sun-line text-lg' : 'ri-moon-line text-lg'} />
    </button>
  );

  return (
    <div className="flex h-screen bg-cream dark:bg-midnight-deep">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-60 bg-midnight dark:bg-midnight-sidebar transform transition-transform duration-200 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } flex flex-col`}
      >
        {/* Logo area */}
        <div className="h-16 flex items-center gap-3 px-5 border-b border-white/5">
          <img src="/icon-192.png" alt="Zman logo" className="w-9 h-9 rounded-lg flex-shrink-0 ring-1 ring-gold/30" />
          <div className="min-w-0">
            <h1 className="text-base font-display font-bold text-white leading-tight truncate">Zman</h1>
            <p className="text-[0.65rem] font-medium tracking-wide text-gold/80">by CodeLab</p>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer border-l-[3px] ${
                  isActive
                    ? 'bg-gold/10 text-gold border-gold'
                    : 'text-slate-300 hover:bg-white/10 hover:text-white border-transparent'
                }`}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className={`${item.icon} text-[1.05rem] ${isActive ? 'text-gold' : 'text-slate-400'}`} />
                </span>
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Gold hairline divider */}
        <div className="mx-4 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />

        {/* User section */}
        <div className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gold to-gold-dark flex items-center justify-center text-midnight text-xs font-bold flex-shrink-0">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-white truncate">
                {user?.user_metadata?.full_name || user?.email?.split('@')[0]}
              </p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
            <button
              onClick={() => signOut()}
              className="w-8 h-8 flex items-center justify-center rounded-md text-slate-400 hover:text-gold hover:bg-white/10 transition cursor-pointer whitespace-nowrap"
              title="Sign out"
            >
              <i className="ri-logout-box-r-line" />
            </button>
          </div>

          {/* Powered by CodeLab */}
          <a
            href="https://codelabsus.com"
            target="_blank"
            rel="noreferrer"
            className="mt-3 flex items-center justify-center gap-1.5 text-[0.65rem] text-slate-500 hover:text-slate-300 transition"
          >
            <i className="ri-terminal-box-line text-electric text-xs" />
            Powered by CodeLab
          </a>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
        {/* Mobile top bar */}
        <div className="lg:hidden h-14 flex items-center gap-3 px-4 border-b border-background-300/40 bg-surface dark:bg-midnight-card flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-200 transition cursor-pointer"
            aria-label="Open menu"
          >
            <i className="ri-menu-line text-xl" />
          </button>
          <img src="/icon-192.png" alt="Zman logo" className="w-7 h-7 rounded-md flex-shrink-0" />
          <span className="text-base font-display font-bold text-foreground-950">Zman</span>
          <div className="ml-auto">{themeToggle}</div>
        </div>

        {children}
      </div>
    </div>
  );
}
